import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Camera, MapPin, Users, Heart, ArrowRight } from "lucide-react";

/**
 * /share/share-a-trip — Share a Trip / Photo reel (iter182).
 *
 * Distinct from the older ShareYourTripReel — this one focuses on the
 * two-tap flow (open app → tap +) rather than the outcome. Designed
 * to onboard first-timers who don't know the app has a photo wall.
 */
const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const LINK = `${PUBLIC_URL}/community?ref=share-trip-reel`;

function Scene({ children, bg, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-[360px] h-[640px] mx-auto flex flex-col justify-between overflow-hidden text-white"
        style={{ background: bg }}
      >
        {children}
      </section>
    </div>
  );
}

export default function ShareTripPhotoReel() {
  return (
    <div className="min-h-screen bg-[#f7f2ec] py-10 px-4">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-display text-4xl mb-2 text-center">Share A Trip · Photo Reel</h1>
        <p className="text-center text-sm text-[#666] mb-8">3 scenes for anyone who's never posted in-app</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Scene label="Scene 1 · Your trip" bg="linear-gradient(180deg,#2a5b2a 0%,#0e2b0e 100%)">
            <div className="p-6 pt-10">
              <div className="inline-block bg-white text-[#0a7f3f] text-[10px] px-2 py-1 uppercase tracking-widest font-black mb-6">Share your camp</div>
              <h2 className="font-display text-[42px] leading-none mb-4">Every trip<br/>tells<br/>a story.</h2>
              <p className="text-white/85 text-sm">Post it in seconds. See who's camping where.</p>
            </div>
            <div className="p-6 flex gap-2 pb-10">
              {[MapPin, Camera, Heart].map((Icon, i) => (
                <div key={i} className="w-12 h-12 bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
              ))}
            </div>
          </Scene>

          <Scene label="Scene 2 · Two taps" bg="#ff4500">
            <div className="p-6 pt-10">
              <div className="inline-block bg-white/25 backdrop-blur-sm text-white text-[10px] px-2 py-1 uppercase tracking-widest font-black mb-5">Two taps</div>
              <h2 className="font-display text-[38px] leading-tight mb-6">Post your trip<br/>in 20 seconds.</h2>
              <div className="space-y-3">
                <div className="bg-white/15 backdrop-blur-sm px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 bg-white text-[#ff4500] font-black text-lg flex items-center justify-center">1</div>
                  <div className="text-sm font-bold">Open Community → tap the &ldquo;+&rdquo;</div>
                </div>
                <div className="bg-white/15 backdrop-blur-sm px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 bg-white text-[#ff4500] font-black text-lg flex items-center justify-center">2</div>
                  <div className="text-sm font-bold">Drop a photo + a line. Done.</div>
                </div>
              </div>
            </div>
            <div className="p-6 text-[11px] mono uppercase tracking-widest text-white/85">No filters, no algorithms, just campers.</div>
          </Scene>

          <Scene label="Scene 3 · Join the wall" bg="linear-gradient(160deg,#0e0e0e 0%,#4a2a1a 100%)">
            <div className="p-6 pt-10">
              <h2 className="font-display text-[40px] leading-none mb-3">Join the<br/><span className="text-[#ff9260]">campfire</span>.</h2>
              <p className="text-white/85 text-sm inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> Free · community-first · UK-owned.</p>
            </div>
            <div className="p-6 flex flex-col items-center gap-2 pb-10">
              <div className="bg-white p-2"><QRCodeSVG value={LINK} size={130} /></div>
              <div className="text-[10px] mono uppercase tracking-widest text-white/70">camp-compass.uk/community</div>
              <div className="inline-flex items-center gap-1.5 text-sm font-bold mt-1"><ArrowRight className="w-4 h-4" /> Share your first trip</div>
            </div>
          </Scene>
        </div>

        <div className="mt-10 p-5 bg-white border hairline max-w-3xl mx-auto">
          <div className="eyebrow mb-2">Caption</div>
          <p className="text-sm">📸 Camped somewhere brilliant this week? Share the photo (and the pitch fee) with 22,000+ other UK campers on CampCompass. Two taps, no algorithm, no ads. Free while we grow. Link in bio 🏕️❤️</p>
        </div>
      </div>
    </div>
  );
}
